import { IntegrationConnectInput } from '../types/action';
import { DataType, TokenizedSource } from '../types/resolvers';

import { IBaseEntity } from './base.entity';
import { AuthenticationScheme } from './credential.interface';
import { IConnectIntegration } from './integration.interface';
import { IProject } from './project.interface';
import { RequestAuthorization } from './steps';

export interface ICustomIntegration extends IBaseEntity {
  /**
   * the project that authored and maintains this custom integration.
   */
  project: IProject;

  /**
   * The ID of the project that authored and maintains this custom integration.
   */
  projectId: string;

  name?: string;

  /**
   * a slug generated from the name of the custom integration (e.g. `custom.github`)
   * it's stripped of non-alphanumeric characters and lowercased
   * they're unique between projects
   */
  readonly slug?: string;

  /**
   * The CDN path of the custom integration's SVG-format icon file.
   */
  icon?: string;

  /**
   * Instances of this custom integration configured for a particular project.
   *
   * Currently, we only expect that one integration will be associated with a custom integration
   * at a time. However, this is subject to change and should not be treated as an invariant in
   * code (hence the array typing).
   */
  integrations: IConnectIntegration[];

  /**
   * Basic (API keys) and OAuth 2.0 authentication types are supported for custom integrations.
   */
  authenticationType?: AuthenticationScheme;

  inputFields: IntegrationConnectInput[];

  oauthAuthorizationUrl?: TokenizedSource<DataType.STRING>;

  oauthAccessTokenUrl?: TokenizedSource<DataType.STRING>;

  oauthScopes?: string;

  oauthIncludeClientAuthorizationHeader?: boolean;
  usePKCEInCodeExchange: boolean;

  apiBaseUrl?: TokenizedSource<DataType.STRING>;
  testEndpointPath?: TokenizedSource<DataType.STRING>;

  apiAuthorization: RequestAuthorization;

  isPublished: boolean;
}

export interface IPublishedCustomIntegration extends ICustomIntegration {
  isPublished: true;
  name: string;
  readonly slug: string;
  icon: string;
  authenticationType: AuthenticationScheme;
  oauthClientId: string;
  oauthScopes: string;
}

export function generateSlugForIntegration(
  customIntegration: ICustomIntegration,
): string | undefined {
  if (!customIntegration.name) {
    return undefined;
  }

  const slug: string | undefined = customIntegration.name
    ? customIntegration.name
        .replace(/[\W_]+/g, '')
        .trim()
        .toLowerCase()
    : undefined;
  return `custom.${slug}`;
}
